import type { Routine, RoutineKind, ScheduleSpec } from "./routines-queries";

export const SCHEDULE_PRESETS: Array<{
  id: string;
  label: string;
  // Builds a *partial* ScheduleSpec for the given user-picked time. The dialog
  // owns the actual time picker; this preset only contributes the kind +
  // structure.
  build: (time: string, weekday: number) => { kind: RoutineKind; spec: Record<string, unknown> };
  // Whether this preset shows the HH:MM time picker (interval-based ones don't).
  needsTime: boolean;
  // Whether this preset shows the weekday picker (only weekly does).
  needsWeekday: boolean;
}> = [
  {
    id: "every_minute",
    label: "каждую минуту",
    needsTime: false,
    needsWeekday: false,
    build: () => ({ kind: "interval", spec: { minutes: 1 } }),
  },
  {
    id: "every_5_minutes",
    label: "каждые 5 минут",
    needsTime: false,
    needsWeekday: false,
    build: () => ({ kind: "interval", spec: { minutes: 5 } }),
  },
  {
    id: "every_hour",
    label: "каждый час",
    needsTime: false,
    needsWeekday: false,
    build: () => ({ kind: "interval", spec: { minutes: 60 } }),
  },
  {
    id: "weekdays",
    label: "каждый рабочий день",
    needsTime: true,
    needsWeekday: false,
    build: (time) => ({ kind: "weekdays", spec: { time } }),
  },
  {
    id: "daily",
    label: "ежедневно",
    needsTime: true,
    needsWeekday: false,
    build: (time) => ({ kind: "daily", spec: { time } }),
  },
  {
    id: "weekly",
    label: "еженедельно",
    needsTime: true,
    needsWeekday: true,
    build: (time, weekday) => ({ kind: "weekly", spec: { weekday, time } }),
  },
  {
    id: "webhook",
    label: "по вебхуку",
    needsTime: false,
    needsWeekday: false,
    build: () => ({ kind: "webhook", spec: {} }),
  },
];

export const WEEKDAYS_RU = [
  { id: 0, short: "пн", long: "понедельник" },
  { id: 1, short: "вт", long: "вторник" },
  { id: 2, short: "ср", long: "среда" },
  { id: 3, short: "чт", long: "четверг" },
  { id: 4, short: "пт", long: "пятница" },
  { id: 5, short: "сб", long: "суббота" },
  { id: 6, short: "вс", long: "воскресенье" },
];

const WEEKDAY_PREP = ["в понедельник", "во вторник", "в среду", "в четверг", "в пятницу", "в субботу", "в воскресенье"];

// Russian "каждый/каждую/каждое <day>" — gender + accusative form. Indexed
// by ISO weekday number (0=Mon..6=Sun).
const EVERY_WEEKDAY = [
  "Каждый понедельник",
  "Каждый вторник",
  "Каждую среду",
  "Каждый четверг",
  "Каждую пятницу",
  "Каждую субботу",
  "Каждое воскресенье",
];

/**
 * Recover the typed ScheduleSpec from a routine row. Backend stores the
 * `kind` separately from the rest of the spec — this puts them back together.
 */
export function routineSchedule(r: Routine): ScheduleSpec {
  const spec = r.schedule_spec || {};
  switch (r.schedule_kind) {
    case "interval":
      return { kind: "interval", minutes: Number(spec.minutes ?? 5) };
    case "daily":
      return { kind: "daily", time: String(spec.time ?? "09:00"), tz: spec.tz as string | undefined };
    case "weekdays":
      return { kind: "weekdays", time: String(spec.time ?? "09:00"), tz: spec.tz as string | undefined };
    case "weekly":
      return {
        kind: "weekly",
        weekday: Number(spec.weekday ?? 0),
        time: String(spec.time ?? "09:00"),
        tz: spec.tz as string | undefined,
      };
    case "webhook":
      return { kind: "webhook" };
  }
}

/**
 * Identify which preset a routine matches, so the edit dialog can re-select
 * the right item in the dropdown without hand-rolling a comparator.
 */
export function presetIdForRoutine(r: Routine): string {
  const spec = routineSchedule(r);
  switch (spec.kind) {
    case "interval":
      if (spec.minutes === 1) return "every_minute";
      if (spec.minutes === 5) return "every_5_minutes";
      if (spec.minutes === 60) return "every_hour";
      return "every_5_minutes";
    case "weekdays":
      return "weekdays";
    case "daily":
      return "daily";
    case "weekly":
      return "weekly";
    case "webhook":
      return "webhook";
  }
}

/** "Каждый рабочий день в 09:00" / "Каждые 5 минут" / "По вебхуку" */
export function describeSchedule(r: Routine): string {
  const spec = routineSchedule(r);
  switch (spec.kind) {
    case "interval":
      if (spec.minutes === 1) return "Каждую минуту";
      if (spec.minutes === 60) return "Каждый час";
      return `Каждые ${spec.minutes} мин`;
    case "weekdays":
      return `Каждый рабочий день в ${spec.time}`;
    case "daily":
      return `Ежедневно в ${spec.time}`;
    case "weekly": {
      const phrase = EVERY_WEEKDAY[spec.weekday] ?? "Еженедельно";
      return `${phrase} в ${spec.time}`;
    }
    case "webhook":
      return "По вебхуку";
  }
}

/**
 * "Следующий: завтра в 09:00" / "Следующий: в понедельник в 10:00" /
 * "По вебхуку". Computed against `next_run_at` from the server, not from
 * scratch — single source of truth.
 */
export function describeNextRun(r: Routine, now: Date = new Date()): string | null {
  if (r.schedule_kind === "webhook") return "По вебхуку";
  if (!r.next_run_at) return null;
  const next = new Date(r.next_run_at);
  if (Number.isNaN(next.getTime())) return null;
  const todayKey = ymd(now);
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const dayDiff = Math.round(
    (startOfDay(next).getTime() - startOfDay(now).getTime()) / (24 * 3600 * 1000),
  );
  const time = next.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  const ymdNext = ymd(next);
  if (ymdNext === todayKey) return `Следующий: сегодня в ${time}`;
  if (ymdNext === ymd(tomorrow)) return `Следующий: завтра в ${time}`;
  if (dayDiff > 0 && dayDiff < 7) {
    const wd = WEEKDAY_PREP[(next.getDay() + 6) % 7]; // JS Sunday=0 → shift to Mon=0
    return `Следующий: ${wd} в ${time}`;
  }
  const date = next.toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
  return `Следующий: ${date} в ${time}`;
}

function ymd(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}
